import { useRouter } from 'next/router'

import { Button } from '@/components/Button'
import { FullScreenLoading } from '@/components/FullScreenLoading'
import { Warning } from '@/components/Warning'

import { useEntry } from '../Theme.hooks'

type Props = {
	className?: string
}

export const ThemeCard = ({ className }: Props) => {
	const { locale = 'en-US' } = useRouter()

	const entry = useEntry()

	const currency: Record<string, string> = {
		'en-US': 'USD',
		'en-AU': 'AUD',
		'en-GB': 'GBP'
	}

	if ('error' in entry) {
		return <Warning className={className}>{entry.error}</Warning>
	}

	if (entry?.isLoading) return <FullScreenLoading />

	const price =
		entry.data?.price && parseInt(entry.data?.price) > 0
			? 'Buy ' +
			  new Intl.NumberFormat(locale, {
					style: 'currency',
					currency: currency[locale]
			  }).format(parseInt(entry.data?.price) / 100)
			: 'Free'

	const purchaseURL = `https://login.bigcommerce.com/deep-links/marketplace/themes/${entry.data?.registryId}`
	const previewURL = `https://login.bigcommerce.com/deep-links/page-builder?variationId=${entry.data?.registryId}`

	return (
		<section className="w-full">
			<div
				className={`w-full flex flex-col md:flex-row items-start md:items-center bg-white shadow-lg rounded-xl relative p-9 gap-4 ${className}`}
			>
				{/* Text Content */}
				<div className="flex flex-col">
					<h1 className="text-bc-black text-2xl font-medium my-2 md:mt-0 md:mb-2">
						{entry.data?.title}
					</h1>

					<div>
						Developed and supported by{' '}
						{entry.data?.contactUrl ? (
							<Button
								link={{
									href: entry.data?.contactUrl ?? ''
								}}
								shouldSkipTrailingSlash={!!entry.data?.contactUrl}
								variant="subtle"
								color="primary"
								size="large"
							>
								{entry.data?.partnerName}
							</Button>
						) : (
							<p className="text-bc-blue font-medium">{entry.data?.partnerName}</p>
						)}
						.
					</div>
				</div>

				{/* Buttons */}
				<div className="md:ml-auto flex flex-col gap-y-4 gap-x-3 md:flex-row text-xs font-medium text-center">
					<Button
						link={{
							href: purchaseURL
						}}
						color="secondary"
						dataTestId="buy-theme-btn"
					>
						{price}
					</Button>

					<Button
						link={{
							href: previewURL
						}}
						variant="outlined"
						color="secondary"
						className="justify-center"
					>
						Preview
					</Button>

					{entry.data?.demoUrl && (
						<Button
							link={{
								href: entry.data.demoUrl
							}}
							variant="outlined"
							color="secondary"
							className="justify-center"
							dataTestId="view-demo-btn"
						>
							View Demo
						</Button>
					)}
				</div>
			</div>
		</section>
	)
}
